import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Typography,
  Space,
  message,
  Alert,
  Popconfirm,
  DatePicker,
  Row,
  Col,
  Tabs
} from 'antd';
import { PlusOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ClearOutlined } from '@ant-design/icons';
import { policyAPI } from '../utils/api';
import { Policy, CreatePolicyRequest, UpdatePolicyRequest } from '../types';
import dayjs from 'dayjs';
import JSON5 from 'json5';
import StrategyVisualEditor from '../components/StrategyVisualEditor';


const { Title } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const Policies: React.FC = () => {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [filteredPolicies, setFilteredPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  
  // 策略数据状态（用于可视化编辑器）
  const [strategyData, setStrategyData] = useState<any>(null);
  const [editMode, setEditMode] = useState<'visual' | 'json'>('visual');

  // 搜索条件状态
  const [searchFilters, setSearchFilters] = useState({
    id: '',
    name: '',
    description: '',
    value: '',
    dateRange: null as any
  });

  useEffect(() => {
    fetchPolicies();
  }, []);

  // 当policies数据变化时，重新应用过滤器
  useEffect(() => {
    applyFilters(searchFilters);
  }, [policies]);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await policyAPI.getPolicies();
      setPolicies(data);
      setFilteredPolicies(data); // 初始化过滤后的数据
    } catch (err: any) {
      setError(err.message || '获取管控措施列表失败');
      console.error('获取管控措施列表失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 搜索过滤逻辑
  const applyFilters = (filters: typeof searchFilters) => {
    let filtered = [...policies];

    // ID过滤
    if (filters.id) {
      filtered = filtered.filter(policy =>
        policy.id.toString().includes(filters.id)
      );
    }

    // 名称过滤
    if (filters.name) {
      filtered = filtered.filter(policy =>
        policy.name.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    // 描述过滤
    if (filters.description) {
      filtered = filtered.filter(policy =>
        policy.description.toLowerCase().includes(filters.description.toLowerCase())
      );
    }

    // 配置值过滤
    if (filters.value) {
      filtered = filtered.filter(policy =>
        policy.value.toLowerCase().includes(filters.value.toLowerCase())
      );
    }

    // 日期范围过滤
    if (filters.dateRange && filters.dateRange.length === 2) {
      const [startDate, endDate] = filters.dateRange;
      filtered = filtered.filter(policy => {
        const createdAt = dayjs(policy.createdAt);
        return createdAt.isAfter(startDate.startOf('day')) &&
               createdAt.isBefore(endDate.endOf('day'));
      });
    }

    setFilteredPolicies(filtered);
  };

  // 处理搜索
  const handleSearch = (values: any) => {
    const filters = {
      id: values.id || '',
      name: values.name || '',
      description: values.description || '',
      value: values.value || '',
      dateRange: values.dateRange || null
    };
    setSearchFilters(filters);
    applyFilters(filters);
  };

  // 重置搜索
  const handleResetSearch = () => {
    const emptyFilters = {
      id: '',
      name: '',
      description: '',
      value: '',
      dateRange: null
    };
    setSearchFilters(emptyFilters);
    searchForm.resetFields();
    setFilteredPolicies(policies);
  };

  // 生成 GUID
  const generateGuid = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  // 移除时间段中的 allDay 标记，避免保存到后端 JSON
  const stripAllDayFlag = (strategy: any) => {
    if (!strategy) return strategy;
    const cloned = JSON.parse(JSON.stringify(strategy));

    const cleansePolicies = (policies: any) => {
      if (!Array.isArray(policies)) return policies;
      return policies.map((policy: any) => {
        if (!policy) return policy;
        const ranges = Array.isArray(policy.appliedTimeRanges)
          ? policy.appliedTimeRanges.map((range: any) => {
              if (!range) return range;
              const { allDay, ...rest } = range;
              return rest;
            })
          : policy.appliedTimeRanges;
        return { ...policy, appliedTimeRanges: ranges || [] };
      });
    };

    if (cloned?.result) {
      cloned.result.policies = cleansePolicies(cloned.result.policies) || cloned.result.policies;
    }
    if (cloned.policies) {
      cloned.policies = cleansePolicies(cloned.policies);
    }

    return cloned;
  };

  // 默认策略数据结构（带 result 包装）
  const getDefaultStrategyData = () => ({
    result: {
      strategies: {
        queryPolicyInterval: '600',
        appAllowance: [],
        appControl: [],
        deviceConfig: [],
        network: [],
        appHidden: []
      },
      policies: []
    },
    targetUrl: null,
    success: true,
    error: null,
    unAuthorizedRequest: false,
    __abp: true
  });

  const showModal = (policy?: Policy) => {
    setEditingPolicy(policy || null);
    setModalVisible(true);

    // 使用 setTimeout 确保 Modal 完全渲染后再设置表单值
    setTimeout(() => {
      if (policy) {
        form.setFieldsValue({
          name: policy.name,
          description: policy.description,
          value: policy.value,
        });
        
        // 解析策略数据用于可视化编辑
        try {
          const parsedData = JSON5.parse(policy.value);
          // 检查是否有 result 包装，如果有则使用，否则添加包装
          if (parsedData.result) {
            setStrategyData(stripAllDayFlag(parsedData));
          } else {
            // 兼容旧格式：没有 result 包装的数据
            setStrategyData(stripAllDayFlag({
              result: parsedData,
              targetUrl: null,
              success: true,
              error: null,
              unAuthorizedRequest: false,
              __abp: true
            }));
          }
        } catch (e) {
          console.error('解析策略数据失败:', e);
          setStrategyData(getDefaultStrategyData());
        }
      } else {
        form.resetFields();
        setStrategyData(getDefaultStrategyData());
      }
      setEditMode('visual'); // 默认使用可视化编辑模式
    }, 0);
  };

  const handleCancel = () => {
    setModalVisible(false);
    setEditingPolicy(null);
    setStrategyData(null);
    form.resetFields();
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);

      // 根据编辑模式处理配置值
      let normalizedValue: string;
      
      if (editMode === 'visual') {
        // 可视化模式：直接使用 strategyData
        if (!strategyData) {
          message.error('策略数据为空');
          setSubmitting(false);
          return;
        }
        normalizedValue = JSON.stringify(stripAllDayFlag(strategyData));
      } else {
        // JSON 模式：解析并验证
        try {
          const parsed = JSON5.parse(values.value);
          normalizedValue = JSON.stringify(stripAllDayFlag(parsed));
        } catch (e: any) {
          message.error(`配置值不是有效的 JSON/JSON5：${e?.message || e}`);
          setSubmitting(false);
          return;
        }
      }

      const payload = { 
        name: values.name,
        description: values.description,
        value: normalizedValue 
      };

      if (editingPolicy) {
        // 更新策略
        const updateData: UpdatePolicyRequest = {
          id: editingPolicy.id,
          ...payload
        };
        await policyAPI.updatePolicy(updateData);
        message.success('管控措施更新成功');
      } else {
        // 创建策略
        const createData: CreatePolicyRequest = payload;
        await policyAPI.createPolicy(createData);
        message.success('管控措施创建成功');
      }

      setModalVisible(false);
      setEditingPolicy(null);
      form.resetFields();
      await fetchPolicies();
    } catch (err: any) {
      message.error(err.message || '操作失败');
      console.error('操作失败:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await policyAPI.deletePolicy(id);
      message.success('管控措施删除成功');
      await fetchPolicies();
    } catch (err: any) {
      message.error(err.message || '删除失败');
      console.error('删除失败:', err);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a: Policy, b: Policy) => a.id - b.id,
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Policy, b: Policy) => a.name.localeCompare(b.name),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-',
      sorter: (a: Policy, b: Policy) => {
        const descA = a.description || '';
        const descB = b.description || '';
        return descA.localeCompare(descB);
      },
    },
    {
      title: '配置值',
      dataIndex: 'value',
      key: 'value',
      render: (text: string) => (
        <div style={{
          maxWidth: 300,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {text || '-'}
        </div>
      ),
      sorter: (a: Policy, b: Policy) => {
        const valueA = a.value || '';
        const valueB = b.value || '';
        return valueA.localeCompare(valueB);
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a: Policy, b: Policy) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      render: (_: any, record: Policy) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
            size="small"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个管控措施吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              icon={<DeleteOutlined />}
              danger
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (error) {
    return (
      <Alert
        message="加载失败"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px 0' }}
      />
    );
  }

  return (
    <div>
      <div className="page-header">
        <Title level={2} style={{ margin: 0 }}>
          🛡️ 管控措施列表
        </Title>
        <p style={{ margin: '8px 0 0 0', color: '#666' }}>
          管理设备管控策略和配置
        </p>
      </div>

      <Card
        className="content-card"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchPolicies}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
            >
              新增管控措施
            </Button>
          </Space>
        }
      >
        {/* 搜索表单 */}
        <Card
          size="small"
          title="搜索条件"
          style={{ marginBottom: 16 }}
          bodyStyle={{ paddingBottom: 0 }}
        >
          <Form
            form={searchForm}
            onFinish={handleSearch}
            layout="inline"
            colon={false}
          >
            <Row gutter={16} style={{ width: '100%' }}>
              <Col span={6}>
                <Form.Item
                  name="id"
                  label="ID"
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder="请输入ID"
                    allowClear
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="name"
                  label="名称"
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder="请输入名称关键词"
                    allowClear
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="description"
                  label="描述"
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder="请输入描述关键词"
                    allowClear
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="value"
                  label="配置值"
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder="请输入配置值关键词"
                    allowClear
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16} style={{ width: '100%' }}>
              <Col span={8}>
                <Form.Item
                  name="dateRange"
                  label="创建时间"
                  style={{ marginBottom: 8 }}
                >
                  <RangePicker
                    style={{ width: '100%' }}
                    placeholder={['开始日期', '结束日期']}
                  />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item label=" " style={{ marginBottom: 8 }}>
                  <Space>
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      htmlType="submit"
                    >
                      搜索
                    </Button>
                    <Button
                      icon={<ClearOutlined />}
                      onClick={handleResetSearch}
                    >
                      重置
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>
        <Table
          columns={columns}
          dataSource={filteredPolicies}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range?.[0]}-${range?.[1]} 条，共 ${total} 条记录`,
          }}
          className="policy-table"
        />
      </Card>

      <Modal
        title={editingPolicy ? '编辑管控措施' : '新增管控措施'}
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={1200}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          preserve={false}
        >
          <Form.Item
            name="name"
            label="名称"
            rules={[
              { required: true, message: '请输入管控措施名称' },
              { max: 100, message: '名称长度不能超过100个字符' }
            ]}
          >
            <Input placeholder="请输入管控措施名称" />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
            rules={[
              { max: 500, message: '描述长度不能超过500个字符' }
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder="请输入管控措施描述"
            />
          </Form.Item>

          <Form.Item label="配置值">
            <Tabs 
              activeKey={editMode} 
              onChange={(key) => {
                setEditMode(key as 'visual' | 'json');
                // 切换到 JSON 模式时，同步数据到表单
                if (key === 'json' && strategyData) {
                  form.setFieldsValue({
                    value: JSON.stringify(stripAllDayFlag(strategyData), null, 2)
                  });
                }
                // 切换到可视化模式时，尝试解析 JSON
                if (key === 'visual') {
                  const jsonValue = form.getFieldValue('value');
                  if (jsonValue) {
                    try {
                      const parsed = JSON5.parse(jsonValue);
                      setStrategyData(stripAllDayFlag(parsed));
                    } catch (e) {
                      message.warning('JSON 格式有误，无法切换到可视化编辑');
                      setEditMode('json');
                    }
                  }
                }
              }}
            >
              <Tabs.TabPane tab="可视化编辑" key="visual">
                {strategyData && strategyData.result && (
                  <div style={{ 
                    border: '1px solid #d9d9d9', 
                    borderRadius: 4, 
                    padding: 16,
                    maxHeight: '60vh',
                    overflowY: 'auto'
                  }}>
                    <StrategyVisualEditor
                      data={strategyData.result}
                      onChange={(newResultData) => {
                        // 更新时保持包装结构
                        setStrategyData({
                          ...strategyData,
                          result: newResultData
                        });
                      }}
                      generateGuid={generateGuid}
                    />
                  </div>
                )}
              </Tabs.TabPane>
              
              <Tabs.TabPane tab="JSON 编辑" key="json">
                <Form.Item
                  name="value"
                  rules={[
                    { required: editMode === 'json', message: '请输入配置值' },
                    {
                      validator: async (_: any, value: string) => {
                        if (editMode !== 'json') return Promise.resolve();
                        if (!value || !value.trim()) return Promise.resolve();
                        try {
                          JSON5.parse(value);
                          return Promise.resolve();
                        } catch (e) {
                          return Promise.reject(new Error('配置值不是有效的 JSON/JSON5'));
                        }
                      }
                    }
                  ]}
                  noStyle
                >
                  <TextArea
                    rows={15}
                    placeholder="请输入配置值，支持 JSON/JSON5，保存时会标准化为严格 JSON"
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                </Form.Item>
              </Tabs.TabPane>
            </Tabs>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCancel}>
                取消
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
              >
                {editingPolicy ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Policies;
